import { Component, inject } from '@angular/core';
import { OrderFlowUpsellComponent } from '@components/order-flow-upsell/order-flow-upsell.component';
import { MycoplasmaTypes } from '@enums/mycoplasma-types';
import { HasBetterLabShowResponse } from '@models/has-better-lab-show-response';
import { MycoplasmaFaq } from '@models/mycoplasma-faq';
import { MycoplasmaResponse } from '@models/mycoplasma-response';
import { OrderUpsell } from '@models/order-upsell';
import { MycoplasmaService } from '@services/mycoplasma.service';

@Component({
  template: '',
})
export abstract class MycoplasmaBaseComponent extends OrderFlowUpsellComponent {
  public mycoplasmaResponse: MycoplasmaResponse;
  public shouldExpand: boolean = false;

  protected mycoplasmaService = inject(MycoplasmaService);

  public abstract readonly mycoplasmaTest: OrderUpsell;

  /**
   * Gets the Mycoplasma upsell test details.
   */
  public override get upsellTest(): OrderUpsell {
    return this.mycoplasmaTest;
  }

  /**
   * Gets the price of the Mycoplasma test.
   */
  public get mycoplasmaPrice(): number {
    return Number(this.mycoplasmaResponse.price);
  }

  /**
   * Toggles the content of the Mycoplasma FAQ.
   *
   * @param { MycoplasmaFaq } faq the FAQ to toggle
   */
  public toggleFAQ(faq: MycoplasmaFaq): void {
    faq.isExpanded = !faq.isExpanded;
  }

  /**
   * Toggles the content of the Mycoplasma content.
   */
  public toggleContent(): void {
    this.shouldExpand = !this.shouldExpand;
  }

  /**
   * Continues the ordering workflow.
   */
  public continueOrderingWorkflow(): void {
    this.processing = true;
    this.orderService.showOrderBetterLab().subscribe({
      next: this.handleSuccesfulShowOrderBetterLab.bind(this),
      error: this.handleErrorShowOrderBetterLab.bind(this),
    });
  }

  /** {@inheritDoc OrderFlowUpsellComponent.onUpsellSuccess} */
  protected override onUpsellSuccess(): void {
    this.continueOrderingWorkflow();
  }

  /**
   * Gets the content for the Mycoplasma upsell.
   *
   * @param { MycoplasmaTypes } mycoplasmaType The type of Mycoplasma content to fetch
   */
  protected getMycoplasmaContent(mycoplasmaType: MycoplasmaTypes): void {
    this.loadingService.toggleLoader(true);
    this.mycoplasmaService.getMycoplasmaContent(mycoplasmaType).subscribe({
      next: (response) => {
        this.mycoplasmaResponse = response;
        this.mycoplasmaResponse.faq_content.faqs.map((faq) => (faq.isExpanded = false));
        this.loadingService.toggleLoader(false);
      },
      error: () => this.continueOrderingWorkflow(),
    });
  }

  /**
   * Handler for the ShowOrderBetterLab request when it succeeds.
   *
   * @param { HasBetterLabShowResponse } hasBetterLabShowResponse the successful response object
   */
  private handleSuccesfulShowOrderBetterLab(hasBetterLabShowResponse: HasBetterLabShowResponse): void {
    this.loadingService.toggleLoader(false);
    this.processing = false;
    this.storageService.betterLab = !!hasBetterLabShowResponse.has_better_lab;
    this.navigateToNextPage();
  }

  /**
   * Handler for the ShowOrderBetterLab request when it fails.
   */
  private handleErrorShowOrderBetterLab(): void {
    this.loadingService.toggleLoader(false);
    this.processing = false;
    this.navigateToNextPage();
  }
}
